import apiClient from './api';
import {
  Category,
  GetAllCategoriesResponse,
  CreateCategoryRequest,
  CreateCategoryResponse,
  UpdateCategoryRequest,
  UpdateCategoryResponse,
  DeleteCategoryResponse,
  ReorderCategoriesRequest,
  ReorderCategoriesResponse,
  Occasion,
  GetAllExperiencesResponse,
  CreateExperienceRequest,
  CreateExperienceResponse,
  UpdateExperienceRequest,
  UpdateExperienceResponse,
  DeleteExperienceResponse,
  OccasionVendorCountResponse,
} from '../types/category.types';

export const categoryService = {
  // ==================== CATEGORIES ====================
  
  /**
   * Get all categories
   */
  getAllCategories: async (): Promise<GetAllCategoriesResponse> => {
    const response = await apiClient.get<GetAllCategoriesResponse>('/categories-catalogue');
    return response.data;
  },

  /**
   * Get category by ID
   */
  getCategoryById: async (categoryId: string): Promise<{ data: Category }> => {
    const response = await apiClient.get<{ data: Category }>(`/categories-catalogue/${categoryId}`);
    return response.data;
  },

  /**
   * Create new category
   */
  createCategory: async (data: CreateCategoryRequest): Promise<CreateCategoryResponse> => {
    const token = localStorage.getItem('accessToken');
    console.log('🔐 Creating category with auth token:', token ? 'Present' : 'Missing');
    const response = await apiClient.post<CreateCategoryResponse>('/categories-catalogue', data);
    return response.data;
  },

  /**
   * Update category
   */
  updateCategory: async (
    categoryId: string,
    data: UpdateCategoryRequest
  ): Promise<UpdateCategoryResponse> => {
    const token = localStorage.getItem('accessToken');
    console.log('🔐 Updating category with auth token:', token ? 'Present' : 'Missing');
    const response = await apiClient.patch<UpdateCategoryResponse>(
      `/categories-catalogue/${categoryId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete category
   */
  deleteCategory: async (categoryId: string): Promise<DeleteCategoryResponse> => {
    const token = localStorage.getItem('accessToken');
    console.log('🔐 Deleting category with auth token:', token ? 'Present' : 'Missing');
    const response = await apiClient.delete<DeleteCategoryResponse>(
      `/categories-catalogue/${categoryId}`
    );
    return response.data;
  },

  /**
   * Reorder categories
   */
  reorderCategories: async (
    data: ReorderCategoriesRequest
  ): Promise<ReorderCategoriesResponse> => {
    const response = await apiClient.patch<ReorderCategoriesResponse>(
      '/categories-catalogue/reorder',
      data
    );
    return response.data;
  },

  // ==================== EXPERIENCES (OCCASIONS) ====================

  /**
   * Get all experiences (occasions) with pagination
   */
  getAllExperiences: async (page: number = 1, limit: number = 10, pageBased: boolean = true): Promise<GetAllExperiencesResponse> => {
    const response = await apiClient.get<GetAllExperiencesResponse>('/occasions', {
      params: { page, limit, pageBased }
    });
    return response.data;
  },

  /**
   * Get experience by ID
   * API returns { success, message, data: Occasion }; we unwrap to return the occasion.
   */
  getExperienceById: async (experienceId: string): Promise<Occasion> => {
    const response = await apiClient.get<{ data: Occasion } | Occasion>(
      `/occasions/${experienceId}`
    );
    const body = response.data;
    if (body && typeof body === 'object' && 'data' in body && body.data) {
      return body.data;
    }
    return body as Occasion;
  },

  /**
   * Create new experience (occasion)
   */
  createExperience: async (
    data: CreateExperienceRequest
  ): Promise<CreateExperienceResponse> => {
    const response = await apiClient.post<CreateExperienceResponse>('/occasions', data);
    return response.data;
  },

  /**
   * Update experience (occasion)
   */
  updateExperience: async (
    experienceId: string,
    data: UpdateExperienceRequest
  ): Promise<UpdateExperienceResponse> => {
    const response = await apiClient.patch<UpdateExperienceResponse>(
      `/occasions/${experienceId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete experience (occasion)
   */
  deleteExperience: async (experienceId: string): Promise<DeleteExperienceResponse> => {
    const response = await apiClient.delete<DeleteExperienceResponse>(
      `/occasions/${experienceId}`
    );
    return response.data;
  },

  /**
   * Get vendor count for an occasion
   */
  getOccasionVendorCount: async (occasionId: string): Promise<OccasionVendorCountResponse> => {
    const response = await apiClient.get<OccasionVendorCountResponse>(
      `/occasions/vendors/count`,
      { params: { occasionId } }
    );
    return response.data;
  },

  /**
   * Reorder experiences (occasions)
   */
  reorderExperiences: async (
    occasions: Array<{ occasionId: string; displayOrder: number }>
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.patch<{ success: boolean; message: string }>(
      '/occasions/reorder',
      { occasions }
    );
    return response.data;
  },
};

// Vendors by type (for category details display order)
export interface VendorByTypeItem {
  vendorId: string;
  companyName?: string;
  profileImageUrl?: string;
  displayOrder: number;
  vendorCreatedAt: number;
}

export interface VendorsByTypeResponse {
  vendors: VendorByTypeItem[];
}

export const catalogueVendorsByTypeService = {
  getVendorsByType: async (type: string, item?: string): Promise<VendorsByTypeResponse> => {
    const params: Record<string, string> = { type };
    if (item) params.item = item;
    const response = await apiClient.get<{ data?: VendorsByTypeResponse; vendors?: VendorByTypeItem[] }>(
      '/catalogue/vendors-by-type',
      { params }
    );
    const data = response.data as any;
    return { vendors: data?.vendors ?? data?.data?.vendors ?? [] };
  },

  reorderVendorsByType: async (type: string, vendorIds: string[], item?: string): Promise<void> => {
    await apiClient.put('/catalogue/vendors-by-type/reorder', { type, vendorIds, ...(item ? { item } : {}) });
  },
};
